import { noop, ServerError } from "@flaminem/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useRef } from "react";
import { useForm, Control, UseFormReturn, UseFormGetValues } from "react-hook-form";
import { object, array, string } from "zod";

import { PropertyValueInput } from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { useGetBlacklistSchemasQuery } from "src/graphql/anti-fraud/queries/blacklists/BlacklistSchemasQuery.ts";
import { useGetBlacklistableIdentifiers } from "src/graphql/anti-fraud/queries/cases/GetCaseBlacklistableIdentifiersQuery";
import { useT } from "src/internationalization";
import { mapToBlacklistableCaseIdentifier } from "src/mappers/case.mappers.ts";
import { BlacklistableCaseIdentifier } from "src/types/case";
import { ClientSchemaBlacklist } from "src/types/clientSchema";

export type BlacklistIdentifiersFormControl = {
  control: Control<BlacklistIdentifiersFormData>;
  submit: (
    onValid: (data: BlacklistIdentifiersFormData) => void
  ) => (e?: React.BaseSyntheticEvent) => Promise<void> | void;
  reset: () => void;
  hasError: boolean;
  methods: UseFormReturn<BlacklistIdentifiersFormData>;
  getValues: UseFormGetValues<BlacklistIdentifiersFormData>;
  blacklistableIdentifiers: {
    data: BlacklistableCaseIdentifier[];
    loading: boolean;
    error: ServerError | null;
  };
  blacklistSchemas: ClientSchemaBlacklist[];
};

export type SelectedIdentifier = {
  id: string;
  identifierCode: string;
  elementCode: string;
  blacklistCode: string;
  additionalProperties: PropertyValueInput[];
};

export type BlacklistIdentifiersFormData = {
  selectedIdentifiers: SelectedIdentifier[];
};

export type UseBlacklistIdentifiersFormProps = {
  caseId: string;
};

export const useBlacklistIdentifiersForm = ({
  caseId,
}: UseBlacklistIdentifiersFormProps): BlacklistIdentifiersFormControl => {
  const t = useT();

  const validationSchema = useMemo(
    () =>
      object({
        selectedIdentifiers: array(
          object({
            id: string(),
            identifierCode: string(),
            elementCode: string(),
            blacklistCode: string(),
            additionalProperties: array(object({ code: string(), value: string() })),
          })
        ),
      }),
    []
  );

  const methods = useForm<BlacklistIdentifiersFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      selectedIdentifiers: [],
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitted },
    reset,
  } = methods;

  const allIdentifiersRef = useRef<SelectedIdentifier[]>([]);

  const { data: blacklistSchemas } = useGetBlacklistSchemasQuery();

  const {
    data: blacklistableIdentifiers,
    loading: loadingBlacklistableIdentifiers,
    error: errorBlacklistableIdentifiers,
  } = useGetBlacklistableIdentifiers({
    variables: {
      id: caseId,
      includeLinkedCaseIdentifiers: true,
    },
    onCompleted: (data) => {
      const identifiers =
        data.case?.blacklistableIdentifiers?.map(mapToBlacklistableCaseIdentifier) ?? [];

      const selectedIdentifiers = identifiers.map(({ id, identifier, element, blacklist }) => ({
        id,
        identifierCode: identifier.schemaIdentifier.code,
        elementCode: element.typeCode,
        blacklistCode: blacklist.code,
        additionalProperties: [],
      }));

      allIdentifiersRef.current = selectedIdentifiers;

      reset({ selectedIdentifiers });
    },
  });

  const hasMissingRequiredDetails = (data: BlacklistIdentifiersFormData): boolean => {
    return data.selectedIdentifiers.some((selected) => {
      const schema = blacklistSchemas.find((s) => s.code === selected.blacklistCode);
      const mandatoryProps = schema?.properties.filter((p) => p.mandatory) ?? [];
      // Source properties are auto-filled from the identifier value — never count as missing
      const sourceCodes = new Set(schema?.sourceProperties.map((p) => p.code) ?? []);
      return mandatoryProps.some(
        (prop) =>
          !sourceCodes.has(prop.code) &&
          !selected.additionalProperties.find((ap) => ap.code === prop.code && ap.value.trim())
      );
    });
  };

  const submit = (onValid: (data: BlacklistIdentifiersFormData) => void | Promise<void>) => {
    if (loadingBlacklistableIdentifiers || errorBlacklistableIdentifiers) return noop;

    if (blacklistableIdentifiers.length === 0)
      return async () => onValid({ selectedIdentifiers: [] });

    return handleSubmit((data) => {
      if (hasMissingRequiredDetails(data)) {
        methods.setError("selectedIdentifiers", {
          type: "incomplete",
          message: t("alerts.confirmFraud.identifiers.validation.incomplete"),
        });
        return;
      }
      onValid(data);
    });
  };

  return {
    control,
    submit,
    reset: () => methods.reset({ selectedIdentifiers: allIdentifiersRef.current }),
    hasError: isSubmitted ? !isValid || !!methods.formState.errors.selectedIdentifiers : false,
    methods,
    blacklistableIdentifiers: {
      data: blacklistableIdentifiers,
      loading: loadingBlacklistableIdentifiers,
      error: errorBlacklistableIdentifiers,
    },
    blacklistSchemas,
    getValues: methods.getValues,
  };
};
